import { Language, PrimaryColor, ThemeMode } from '../constants';

export const LocalStorageKey = {
  language: 'language',
  timers: 'timers',
  theme: 'theme',
  primaryColor: 'primaryColor',
  cookieConsentAccepted: 'cookieConsentAccepted',
  pushFcmToken: 'pushFcmToken',
  pushPermissionPromptCompleted: 'pushPermissionPromptCompleted',
  appInstallPromptCompleted: 'appInstallPromptCompleted',
} as const;

type LocalStorageKeyType = keyof typeof LocalStorageKey;

type LocalStorageValueByKey = {
  [LocalStorageKey.language]: Language;
  [LocalStorageKey.timers]: Record<string, unknown>;
  [LocalStorageKey.theme]: ThemeMode;
  [LocalStorageKey.primaryColor]: PrimaryColor;
  [LocalStorageKey.cookieConsentAccepted]: boolean;
  [LocalStorageKey.pushFcmToken]: string;
  [LocalStorageKey.pushPermissionPromptCompleted]: boolean;
  [LocalStorageKey.appInstallPromptCompleted]: boolean;
};

const defaultValues: LocalStorageValueByKey = {
  [LocalStorageKey.language]: Language.en,
  [LocalStorageKey.timers]: {},
  [LocalStorageKey.theme]: ThemeMode.light,
  [LocalStorageKey.primaryColor]: PrimaryColor.orange,
  [LocalStorageKey.cookieConsentAccepted]: false,
  [LocalStorageKey.pushFcmToken]: '',
  [LocalStorageKey.pushPermissionPromptCompleted]: false,
  [LocalStorageKey.appInstallPromptCompleted]: false,
};

const prefix = 'sg-';
const getLocalStorageKey = (key: LocalStorageKeyType): string => {
  return `${prefix}${key}`;
};

export class LocalStorageService {
  static setValue<K extends LocalStorageKeyType>(
    key: K,
    value: LocalStorageValueByKey[K],
  ): void {
    localStorage.setItem(
      getLocalStorageKey(key),
      typeof value === 'object' ? JSON.stringify(value) : String(value),
    );
  }

  static getValue<K extends LocalStorageKeyType>(
    key: K,
  ): LocalStorageValueByKey[K] | string | null {
    const currentValue = localStorage.getItem(getLocalStorageKey(key));

    if (currentValue === null) {
      const defaultValue = defaultValues[key];
      localStorage.setItem(
        getLocalStorageKey(key),
        typeof defaultValue === 'object'
          ? JSON.stringify(defaultValue)
          : String(defaultValue),
      );
      return defaultValue;
    }

    try {
      return JSON.parse(currentValue) as LocalStorageValueByKey[K];
    } catch (e) {
      return currentValue;
    }
  }

  static removeValue(key: LocalStorageKeyType): void {
    localStorage.removeItem(getLocalStorageKey(key));
  }
}
